
import { useState, useRef } from 'react';

interface FileUploadProps {
  folderId?: string;
  accessLevel?: 'public' | 'basic' | 'premium' | 'admin';
  onUploadComplete?: (document: any) => void;
  className?: string;
  userEmail?: string;
  userAccessLevel?: string;
}

export default function FileUpload({ 
  folderId, 
  accessLevel = 'basic', 
  onUploadComplete,
  className = '',
  userEmail,
  userAccessLevel
}: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Check if user is logged in
    if (!userEmail) {
      setError('Please log in to upload files');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file size (max 50MB)
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`File ${file.name} is too large. Maximum size is 50MB.`);
        }

        const formData = new FormData();
        formData.append('file', file);
        if (folderId) formData.append('folderId', folderId);
        formData.append('accessLevel', accessLevel);
        formData.append('userEmail', userEmail);
        formData.append('userAccessLevel', userAccessLevel || 'basic');

        const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/upload-document', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        const result = await response.json();
        if (onUploadComplete) {
          onUploadComplete(result.document);
        }

        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return 'ri-file-pdf-line';
    if (fileType.includes('word') || fileType.includes('document')) return 'ri-file-word-line';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'ri-file-excel-line';
    if (fileType.includes('image')) return 'ri-image-line';
    return 'ri-file-line';
  };

  return (
    <div className={`bg-white border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#9B1F3A] transition-colors ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.py,.json"
        onChange={handleFileSelect}
        disabled={isUploading}
        className="hidden"
        id="file-upload"
      />
      
      <label 
        htmlFor="file-upload" 
        className={`cursor-pointer ${isUploading ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
          <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
        </div>
        
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {isUploading ? 'Uploading...' : 'Upload Documents'}
        </h3>
        
        <p className="text-sm text-gray-500 mb-4">
          Drag and drop files here, or click to select
        </p>
        
        <p className="text-xs text-gray-400">
          Supports: PDF, Word, Excel, Images, Python, JSON (Max 50MB each)
        </p>
      </label>

      {isUploading && (
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-[#9B1F3A] h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2">{uploadProgress}% Complete</p>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-md">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
